import { isNil } from 'lodash'

const MONEY_REGEX = /^([+-]?)(?:0|[1-9][0-9]{0,2}(?:,?[0-9]{3}){0,3})(\.[0-9]{1,2})?$/
const EURO_MONEY_REGEX = /^([+-]?)(?:0|[1-9][0-9]{0,2}(?:\.?[0-9]{3}){0,3})(,[0-9]{1,})?$/

export const currencyToNumber = (currency: string | number | undefined): number => {
  if (!currency) return 0

  // remove all non-numeric characters apart from [, . -]
  const value = currency.toString().replace(/[^0-9,.-]/g, '')
  let number = value

  if (isEuroFormat(value)) {
    // replace , with . and remove all .
    number = isTensValue(value) ? value : value.replace(/[,.]/g, (x) => (x === ',' ? '.' : ''))
  } else {
    // remove all commas
    number = value.replace(/,/g, '')
  }

  // We use toFixed(2) here to ensure we get a proper number to be converted cents
  return Number(parseFloat(number).toFixed(2))
}

export const isTensValue = (value: string | number | undefined | null): boolean => {
  if (!value) return false

  const symbols = value.toString().match(/[^0-9]/g)

  return !!symbols && symbols.length === 1 && symbols[0] === '.'
}

const isEuroFormat = (currency: string): boolean => {
  return EURO_MONEY_REGEX.test(currency)
}

export const isValidAmount = (amount: string): boolean => {
  if (amount === '') return false

  if (isAmountZero(amount) || isTensValue(amount)) return true

  return EURO_MONEY_REGEX.test(amount) || MONEY_REGEX.test(amount)
}

const isAmountZero = (amount: string | null): boolean => !isNil(amount) && Number(amount) === 0

export const amountToNumber = (amount: string | number | undefined | null) => {
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  if (!isValidAmount(amount as string)) return 0

  if (!amount) return 0

  return currencyToNumber(amount)
}

export const roundTo = function (num: string | number | undefined, decimalPlace = 4) {
  const formattedCurrency = currencyToNumber(num)
  const factor = 10 ** decimalPlace
  const digit = Number(formattedCurrency)

  return Math.round(digit * factor) / factor
}

export const getCurrencySymbolFromCode = (currencyCode: string) => {
  try {
    return (0)
      .toLocaleString('de-DE', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, '')
      .trim()
  } catch (e) {
    console.error('Currency format not supported') // eslint-disable-line no-console
    return '€'
  }
}

export const toCents = (amount: number) => {
  return Math.round(amount * 100)
}

export const fromCents = (amount: number) => {
  return amount / 100
}

/**
 * It validates that the amount value only contains numbers, points, dashes or commas and it start by a number, but it does not check if the amount is a valid currency (for example, 123.444.3123.,4 would return true).
 *
 * This function is being useful for currency amount inputs as it lets us restrict the characters introduce by users so they cannot introduce letters or special characters.
 *
 * If you want to check if a string is a valid amount, you should use the function isValidAmount defined in this file.
 */
export const isValidPartialAmount = (amount: string): boolean => /^(\d|\-)[\d\,\.]*$/.test(amount)
